import { type ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  FileText,
  Heart,
  LayoutDashboard,
  LogOut,
  ShieldAlert,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { clearSession, getAdmin } from '@/lib/auth';
import { Button } from './ui/button';
import { RangePicker } from './RangePicker';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
}

const nav: NavItem[] = [
  { to: '/', label: 'Overview', icon: LayoutDashboard },
  { to: '/users', label: 'Users', icon: Users },
  { to: '/engagement', label: 'Engagement', icon: Heart },
  { to: '/content', label: 'Content', icon: BarChart3 },
  { to: '/moderation', label: 'Moderation', icon: ShieldAlert },
  { to: '/posts', label: 'Posts', icon: FileText },
  { to: '/health', label: 'Health', icon: Activity },
];

export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const admin = getAdmin();
  // Preserve query params (notably ?range=…) when moving between pages.
  // URL search params are scoped to the current location, so a plain
  // `to="/users"` would strip them on navigation.
  const search = location.search;

  function logout(): void {
    clearSession();
    navigate('/login', { replace: true });
  }

  return (
    <div className="grid min-h-full grid-cols-[240px_1fr] bg-surface-bg text-fg">
      <aside className="border-r border-surface-divider bg-surface-raised">
        <div className="flex h-14 items-center gap-3 px-6 border-b border-surface-divider">
          <img src="/malsi-logo.svg" alt="Malsi" className="h-6 w-auto" />
          <span className="text-xs font-bold uppercase tracking-wide text-fg-muted">
            Admin
          </span>
        </div>
        <nav className="py-3">
          {nav.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={{ pathname: to, search }}
              end={to === '/'}
              className={({ isActive }) =>
                cn(
                  'relative flex items-center gap-3 px-6 py-3.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'text-fg before:absolute before:left-0 before:top-1/2 before:h-6 before:w-[2px] before:-translate-y-1/2 before:bg-brand-red'
                    : 'text-fg-subtle hover:bg-surface-bg/50 hover:text-fg',
                )
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex flex-col min-h-full">
        <header className="flex h-14 items-center justify-between border-b border-surface-divider px-6">
          <RangePicker />
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm font-medium leading-tight">
                {admin?.name ?? admin?.email ?? 'Admin'}
              </div>
              {admin?.name && (
                <div className="text-xs text-fg-subtle leading-tight">
                  {admin.email}
                </div>
              )}
            </div>
            <Button variant="ghost" size="icon" onClick={logout} title="Log out">
              <LogOut size={16} strokeWidth={1.5} />
            </Button>
          </div>
        </header>
        <main className="flex-1 p-8">{children}</main>
      </div>
    </div>
  );
}
